import { Product } from "~/domains/product/Product";
import { Box, Button, CardContent, Stack, styled } from "~/common/mui";
import { ProductParams2 } from "~/domains/product/components/ProductParamsV2";
import { type ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { useTranslation } from "react-i18next";
import { isNonNull } from "~/common/utils/reducers";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import { getCountriesList } from "~/domains/product/utils/productDescriptorUtils";
import { getTariffNumbers } from "~/utils/productUtils";
import { Price } from "~/domains/pricing/components/Price/v3/Price";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import { Parameter, ParameterStack } from "~/common/components/ParameterV2";
import type { ResolvedGroupedCartEntry } from "../cart/models/CartData";
import { multiply, volatileSum, zero } from "~/utils/PriceUtils";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { deleteProductSaga } from "../cart/redux/actions/cartQuoteCommonActions";
import { useDispatch } from "react-redux";
import Delete from "~/common/mui/icons/Delete";
import { isTariffSurchargeEnabled } from "../cart/utils/cartUtils";

interface CartEntrySummaryProps {
  product: ProductDescriptor;
  resolvedCartEntry: ResolvedGroupedCartEntry;
  editMode: boolean;
  onDelete: () => void;
}

export const CartEntrySummary = ({ product, resolvedCartEntry, editMode, onDelete }: CartEntrySummaryProps) => {
  const { t } = useTranslation();

  const params = [
    ProductParams2.SchaefflerId({ product, t }),
    {
      label: "Tariff number",
      value: getTariffNumbers(product.base).join(", "),
    },
    {
      label: "Country of origin",
      value: getCountriesList({ product: product.base }).join(", "),
    },
  ].filter((p): p is { label: string; value: string } => isNonNull(p) && !!p.value);

  const flatLineItems = resolvedCartEntry.packGroups.flatMap((group) => group.packEntries);
  const totalQuantity = flatLineItems.reduce((sum, item) => sum + item.quantity, 0);
  const lineItemCount = flatLineItems.length;
  const subTotal = volatileSum(
    ...flatLineItems.map((lineItem) => multiply(lineItem.netPrice ?? zero(), lineItem.quantity)),
  );

  const isOnSale = useSalesHelper(product).isProductOnSale();

  const dispatch = useDispatch();
  const handleDelete = () => {
    onDelete();
    dispatch(deleteProductSaga(product.base.code));
  };

  const hasAnyTariffSurcharge =
    isTariffSurchargeEnabled() && flatLineItems.some((lineItem) => lineItem.netPrice?.tariffSurcharge);
  const hasAnyIncludedSurcharge =
    isTariffSurchargeEnabled() && flatLineItems.some((lineItem) => (lineItem.netPrice?.surcharge ?? 0) > 0);

  return (
    <ProductDescriptorContext value={product}>
      <CardSummary sx={{ position: "relative" }}>
        <Box sx={{ gridArea: "image" }}>
          <Product.PdpLink>
            <Product.PrimaryImageWithLogosV2 size={120} logoWidth={32} gap={16} />
          </Product.PdpLink>
          {isOnSale && <FloatingSaleTag size={32} />}
        </Box>

        <Stack gap={2} sx={{ gridArea: "summary" }}>
          <Stack gap={0.5}>
            <Product.DesignationContainer>
              <Product.PreferredIndicator />
              <Product.PdpLink>
                <Product.DesignationV2 variant="titleMedium" fontWeight={"bold"} />
              </Product.PdpLink>
            </Product.DesignationContainer>
            <Product.MMDChip />
            <Product.ShortDescriptionV2 />
          </Stack>

          <ParameterStack parameterVariant={"row"}>
            {params.map((data) => (
              <Parameter {...data} />
            ))}
          </ParameterStack>
          <Product.ReconditionableTag />
        </Stack>

        <Box sx={{ gridArea: "quantity" }}>
          <Parameter variant="column" label={t("cartPage.table.header.quantity.title")} value={totalQuantity} />
        </Box>

        <Box sx={{ gridArea: "lines" }}>
          <Parameter variant="column" label={t("cartPage.summary.productLines")} value={lineItemCount} />
        </Box>

        <Box sx={{ gridArea: "subtotal", justifySelf: "flex-end", textAlign: "end" }}>
          <Parameter
            variant="column"
            label={t("cartPage.table.subtotal")}
            slotProps={{
              value: {
                sx: {
                  fontWeight: "bold",
                },
              },
            }}
            value={
              <Price
                price={subTotal}
                surchargeEnabled
                tariffSurcharge={hasAnyTariffSurcharge || hasAnyIncludedSurcharge}
              />
            }
          />
        </Box>

        {product.base.successorProduct && (
          <Box sx={{ gridArea: "new-version", justifySelf: "flex-start" }}>
            <NewVersionMessage
              baseProduct={{
                successorProduct: product.base.successorProduct,
              }}
            />
          </Box>
        )}

        {editMode && (
          <Box sx={{ gridArea: "delete-product", justifySelf: "flex-end" }}>
            <Button variant="text" startIcon={<Delete />} color="textPrimary" onClick={handleDelete}>
              {t("cartPage.button.deleteProduct")}
            </Button>
          </Box>
        )}
      </CardSummary>
    </ProductDescriptorContext>
  );
};

const CardSummary = styled(CardContent)(({ theme: _theme }) => ({
  padding: "24px",
  display: "grid",
  columnGap: "32px",
  rowGap: "24px",
  gridTemplateAreas: `"image summary     quantity    lines          subtotal"
                      "image new-version new-version delete-product delete-product"`,
  gridTemplateColumns: "168px 10fr 3fr 3fr 4fr",
}));

const FloatingSaleTag = styled(SaleTag)({
  position: "absolute",
  top: "0",
  left: "0",
});
